<!--
  - SPDX-FileCopyrightText: 2026 Conduction B.V.
  - SPDX-License-Identifier: EUPL-1.2
-->

<template>
	<div
		class="cn-action-buttons"
		:class="{ 'cn-action-buttons--menu': display === 'menu' }"
		data-testid="cn-action-buttons">
		<!-- `display: "menu"` keeps every dialog below but renders no buttons of
		     its own: the host draws the entries as NcActionButtons inside an
		     overflow menu instead, driven by the `entries` event. NcActions
		     keeps only NcAction* vnodes out of its default slot, so this
		     component cannot be placed in the menu itself. -->
		<template v-if="display === 'buttons'">
			<template v-for="entry in visibleActions">
				<!-- Toggle: a stateful two-way state button (GET on mount, write on
				     click, optimistic + revert). Rendered inline, never dispatched. -->
				<NcButton
					v-if="entry.type === 'toggle'"
					:key="entry.id"
					:variant="toggleState[entry.id] ? 'primary' : 'secondary'"
					:disabled="Boolean(togglePending[entry.id])"
					:data-testid="`cn-action-toggle-${entry.id}`"
					:aria-pressed="String(Boolean(toggleState[entry.id]))"
					@click="onToggleClick(entry)">
					<template v-if="entry.icon" #icon>
						<CnIcon v-if="isMdiIconName(entry.icon)" :name="entry.icon" :size="20" />
						<span v-else :class="entry.icon" />
					</template>
					{{ tr(toggleState[entry.id] ? (entry.labelOn || entry.label) : (entry.labelOff || entry.label)) }}
				</NcButton>
				<!-- Everything else: a plain action button routed through the shared
				     dispatcher (api-call / open-form / navigate / open-modal / refresh),
				     confirm-gated first when the action asks for it. -->
				<NcButton
					v-else
					:key="entry.id"
					:type="entry.variant || 'secondary'"
					:disabled="Boolean(actionPending[entry.id])"
					:data-testid="`cn-action-${entry.id}`"
					@click="onActionClick(entry)">
					<template v-if="entry.icon" #icon>
						<CnIcon v-if="isMdiIconName(entry.icon)" :name="entry.icon" :size="20" />
						<span v-else :class="entry.icon" />
					</template>
					{{ tr(entry.label) }}
				</NcButton>
			</template>
		</template>

		<!-- Confirm gate for a confirm:true action (reuses CnConfirmDialog). -->
		<CnConfirmDialog
			v-if="confirmEntry"
			ref="confirmDialog"
			:dialogTitle="tr(confirmEntry.confirmTitle || confirmEntry.label)"
			:message="tr(confirmEntry.confirmMessage) || defaultConfirmMessage"
			:variant="confirmEntry.variant === 'error' ? 'error' : 'primary'"
			@confirm="onConfirmProceed"
			@close="confirmEntry = null" />

		<!-- Schema-driven create dialog for an open-form action. The plain
		     form is the default: the properties/JSON table is a power-user
		     surface, and a header button that says "New case" is aimed at
		     someone filing one, not at someone inspecting the schema. Set
		     `advanced: true` on the action to get the table back. -->
		<CnFormDialog
			v-if="formEntry && formSchema && !formEntry.advanced"
			ref="formDialog"
			:schema="formSchema"
			:item="null"
			:register="formRegister"
			:initialData="formInitialValues || {}"
			:dialogTitle="tr(formEntry.formTitle) || ''"
			:includeFields="formEntry.includeFields || null"
			:excludeFields="formEntry.excludeFields || []"
			:fieldOverrides="formEntry.fieldOverrides || {}"
			:size="formEntry.size || 'normal'"
			:columns="formEntry.columns || 1"
			@confirm="onFormConfirm"
			@close="closeForm" />
		<CnAdvancedFormDialog
			v-if="formEntry && formSchema && formEntry.advanced"
			ref="formDialog"
			:schema="formSchema"
			:item="null"
			:initialValues="formInitialValues"
			@confirm="onFormConfirm"
			@close="closeForm" />

		<!-- run-node's config dialog (manifest-run-node-action): only mounted
		     when the target node declared at least one field AND that
		     describe call succeeded — an empty/no form runs immediately with
		     no dialog at all (openRunNode below), and a describe FAILURE also
		     skips the dialog (toasts instead) rather than showing an empty,
		     confusing one. -->
		<CnRunNodeDialog
			v-if="runNodeEntry && runNodeFields.length"
			:title="tr(runNodeEntry.formTitle) || tr(runNodeEntry.label)"
			:fields="runNodeFields"
			:loading="Boolean(actionPending[runNodeEntry.id])"
			:translate="effectiveTranslate"
			@confirm="onRunNodeConfirm"
			@close="closeRunNode" />
	</div>
</template>

<script>
import { translate as t } from '@nextcloud/l10n'
import { NcButton } from '@nextcloud/vue'
import { inject } from 'vue'
import CnConfirmDialog from '../../dialogs/CnConfirmDialog.vue'
import CnRunNodeDialog from '../../dialogs/CnRunNodeDialog.vue'
import { fetchEndpointSource } from '../../composables/useEndpointSource.js'
import { useObjectStore } from '../../store/useObjectStore.js'
import { buildOnSuccessRoute, dispatchAction, postRunNode, resolveCreateOverrideHandler, resolveObjectOpType } from '../../utils/actionsDispatcher.js'
import { resolveObjectTokenContext } from '../../utils/detailObjectContext.js'
import { usesArrayValues, valueArrayFor, valueRecordsFor } from '../../utils/dynamicProperties.js'
import { resolveFilterTokens } from '../../utils/resolveFilterTokens.js'
import { evaluateVisibleWhen } from '../../utils/visibleWhen.js'
import { CnAdvancedFormDialog } from '../CnAdvancedFormDialog/index.js'
import { CnFormDialog } from '../CnFormDialog/index.js'
import { CnIcon } from '../CnIcon/index.js'

/**
 * CnActionButtons — declarative header-actions surface (#91 Wave 3).
 *
 * Renders a page's `headerActions[]` as buttons and owns the two action
 * behaviours that need STATE or a DIALOG the one-shot `dispatchAction`
 * can't provide on its own:
 *
 *  - **`open-form`** — mounts the shared `CnAdvancedFormDialog` for the
 *    action's `schema` (fetched through the object store), saves on
 *    confirm, toasts, refreshes the page, and optionally navigates to
 *    `onSuccessRoute`. Two optional keys cover the schemas a bare create
 *    cannot satisfy: `props` seeds fixed field values into the create form
 *    (so one schema can back several buttons, each fixing its own
 *    discriminator), and `createOverride` names a registry handler that owns
 *    the persist instead of `objectStore.saveObject` (for a schema requiring
 *    a server-minted field the form cannot supply). `includeFields` /
 *    `excludeFields` / `fieldOverrides` narrow what the button asks for, which
 *    is how one schema serves both a full editor and a quick-create button.
 *    `advanced: true` swaps in the properties/JSON table for a power user.
 *  - **`toggle`** — a two-way state button: `GET`s `stateSource` on mount,
 *    renders `labelOn` / `labelOff`, and on click `writes` the flipped
 *    value OPTIMISTICALLY, reverting on failure.
 *  - **`run-node`** (manifest-run-node-action) — GETs the target flow node's
 *    describe endpoint (OpenRegister's `or-flow-run-node`); a node with
 *    declared fields mounts `CnRunNodeDialog` (its own small field renderer —
 *    see that component's docblock for why it is not `CnFlowNodeEditModal`'s),
 *    a node with none runs immediately. Either way `postRunNode()`
 *    (`actionsDispatcher.js`) makes the actual call, subject-token-resolved
 *    exactly like `agent`.
 *
 * Every other type (`api-call`, `navigate`, `open-modal`, `open-page`,
 * `refresh`, `handler`) routes through the shared `dispatchAction` — with
 * a `CnConfirmDialog` gate first when the action sets `confirm: true`
 * (the object-op precedent: the dispatcher runs AFTER confirmation).
 *
 * Each action may carry a `visibleWhen` predicate (the shared banner
 * shape — `{ endpoint | source | field, op, value }`) evaluated against
 * the page / object context; a hidden action simply doesn't render.
 *
 * Placement: CnDashboardPage / CnDetailPage / CnPageRenderer mount this in
 * their header-actions area, passing the page's `headerActions[]`. The
 * object context (`{ objectId, object, register, schema }`) and the page
 * workspace / app-config bags are injected so tokens + local `visibleWhen`
 * resolve against the current record and page state.
 *
 * ```json
 * "headerActions": [
 *   { "id": "new-lead", "label": "New lead", "type": "open-form",
 *     "register": "crm", "schema": "lead", "onSuccessRoute": "Leads", "variant": "primary" },
 *   { "id": "approve", "label": "Approve", "type": "api-call",
 *     "url": "/apps/shillinq/api/payment-runs/@objectId/approve", "confirm": true,
 *     "visibleWhen": { "field": "state", "op": "eq", "value": "pending" } },
 *   { "id": "toggle-open", "type": "toggle", "labelOn": "Open", "labelOff": "Closed",
 *     "stateSource": { "url": "/apps/pipelinq/api/werkplek/@objectId/state", "responsePath": "open" },
 *     "field": "open", "writeUrl": "/apps/pipelinq/api/werkplek/@objectId/state", "method": "PUT" }
 * ]
 * ```
 */
export default {
	name: 'CnActionButtons',

	components: { NcButton, CnIcon, CnConfirmDialog, CnFormDialog, CnAdvancedFormDialog, CnRunNodeDialog },

	inject: {
		/** Detail-page object context (`{ objectId, object, register, schema }`). */
		cnObjectContext: { default: null },
		/** v2 slot-grid detail context holder (CnPageRenderer). */
		cnDetailObjectContext: { default: null },
		/** Page-level workspace context bag (reactive). */
		cnWorkspaceContext: { default: () => ({}) },
		/** Page-level app config bag (reactive). */
		cnAppConfig: { default: () => ({}) },
		/** Pre-bound dispatchAction from CnPageRenderer (router/registry/handlers/openModal wired). */
		cnDispatchAction: { default: null },
		/**
		 * Host translate function provided by CnAppRoot as
		 * `cnTranslate: this.translate` (bound to the host app's id). The
		 * manifest-authored action labels, confirm copy and api-call toasts
		 * are run through it. Defaults to an identity function so an
		 * untranslated key renders as itself.
		 */
		cnTranslate: { default: () => (key) => key },
		/** v2 component registry, for resolving an `open-form` createOverride. */
		cnRegistry: { default: () => ({}) },
		/** Legacy customComponents map, same resolution fallback. */
		cnCustomComponents: { default: () => ({}) },
	},

	props: {
		/**
		 * The page's declarative header actions. Each entry is a typed action
		 * (`open-form` | `toggle` | `api-call` | `navigate` | `open-modal` |
		 * `refresh` | `handler`) plus `id` / `label` and an optional
		 * `visibleWhen` predicate, `icon`, `variant`, and `confirm`.
		 *
		 * @type {Array<object>}
		 */
		actions: {
			type: Array,
			default: () => [],
		},

		/**
		 * Explicit Vue Router instance for `navigate` / `open-page` /
		 * `onSuccessRoute`. Falls back to `this.$router`. Only needed for
		 * standalone mounts outside a router tree.
		 *
		 * @type {object|null}
		 */
		router: {
			type: Object,
			default: null,
		},

		/**
		 * Where the actions are drawn. `buttons` (the default) puts one
		 * NcButton per action in the host's header. `menu` draws none and
		 * emits `entries` instead, so the host can render them inside an
		 * overflow menu while this component keeps owning the dialogs.
		 *
		 * @type {'buttons'|'menu'}
		 */
		display: {
			type: String,
			default: 'buttons',
			validator: (v) => ['buttons', 'menu'].includes(v),
		},
	},

	emits: ['created', 'entries'],

	setup() {
		// Read the two detail-surface injects once so the object token context
		// resolves identically to the endpoint-bound widgets (Wave 3).
		const objectCtxRaw = inject('cnObjectContext', null)
		const detailCtxRaw = inject('cnDetailObjectContext', null)
		const workspaceRaw = inject('cnWorkspaceContext', null)
		const appConfigRaw = inject('cnAppConfig', null)
		return { objectCtxRaw, detailCtxRaw, workspaceRaw, appConfigRaw }
	},

	data() {
		return {
			/** Evaluated visibleWhen outcome per action id (true when no predicate). */
			visibility: {},
			/** Current boolean state per toggle action id. */
			toggleState: {},
			/** In-flight flag per toggle action id (disables the button). */
			togglePending: {},
			/** In-flight flag per non-toggle action id. */
			actionPending: {},
			/** The action awaiting confirmation (null = no dialog). */
			confirmEntry: null,
			/** The open-form action currently showing its dialog (null = closed). */
			formEntry: null,
			/** The fetched schema object for the open-form dialog (null until loaded). */
			formSchema: null,
			/** The run-node action currently in flight / showing its dialog (null = closed). */
			runNodeEntry: null,
			/** The target node's declared config-form fields, from the describe GET. */
			runNodeFields: [],
			/** Default confirm-dialog message. */
			defaultConfirmMessage: t('nextcloud-vue', 'Are you sure you want to continue?'),
		}
	},

	computed: {
		/**
		 * Effective translate function: the injected `cnTranslate` (the host
		 * app's bound `t()`), identity by default.
		 *
		 * @return {(key: string) => string}
		 */
		effectiveTranslate() {
			return typeof this.cnTranslate === 'function' ? this.cnTranslate : (key) => key
		},

		/**
		 * Create-form seed values for the open `open-form` action. An action
		 * declares them as `props`, which is how a single schema backs several
		 * buttons — "New request" and "New complaint" both open the `ticket`
		 * form, each fixing its own `ticketType`.
		 *
		 * @return {object|null}
		 */
		formInitialValues() {
			const props = this.formEntry && this.formEntry.props
			if (!props || typeof props !== 'object' || Array.isArray(props)) {
				return null
			}
			// Seed values go through the SAME token grammar as filters, so an
			// action on a detail page can stamp the record it belongs to:
			// `{ "domainObjectRef": "@objectId" }`. Without this the literal
			// string "@objectId" is saved, and a foreign key pointing at nothing
			// is a defect that only shows up in whatever reads it later.
			return resolveFilterTokens(props, this.tokenCtx)
		},

		/**
		 * The register the open dialog reads and writes against. The plain
		 * form needs it to resolve `$ref` pickers into real dropdowns; without
		 * one a reference field renders as an empty select and the action is
		 * unusable for exactly the schemas that need it most.
		 *
		 * @return {string} The register slug, '' when none is resolvable.
		 */
		formRegister() {
			return (this.formEntry && this.formEntry.register) || this.objectCtx.register || ''
		},

		/** The actions whose visibleWhen evaluated true (or carry no predicate). */
		visibleActions() {
			return (this.actions || []).filter((a) => a && a.id && this.visibility[a.id] !== false)
		},

		/**
		 * The visible actions flattened for a host that draws them as menu
		 * items. Everything the item needs to render is resolved here — the
		 * translated label a toggle currently shows, its pressed state, its
		 * pending flag, and which of the two icon shapes applies — so the host
		 * template stays a plain v-for and needs none of this component's
		 * state. `run()` is pre-bound, the way CnIndexPage pre-binds a header
		 * action's handler for the index bar, so the host dispatches without
		 * reaching back through a ref.
		 *
		 * @return {Array<object>} One menu-ready descriptor per visible action.
		 */
		menuEntries() {
			return this.visibleActions.map((entry) => {
				const isToggle = entry.type === 'toggle'
				const on = Boolean(this.toggleState[entry.id])
				const label = isToggle
					? (on ? (entry.labelOn || entry.label) : (entry.labelOff || entry.label))
					: entry.label
				return {
					id: entry.id,
					label: this.tr(label),
					// An mdi name goes to CnIcon; a legacy `icon-*` class goes
					// on a bare span. Deciding it here keeps isMdiIconName from
					// having to be duplicated into every host.
					iconName: this.isMdiIconName(entry.icon) ? entry.icon : '',
					iconClass: (entry.icon && !this.isMdiIconName(entry.icon)) ? entry.icon : '',
					disabled: Boolean(isToggle ? this.togglePending[entry.id] : this.actionPending[entry.id]),
					// null, not false: a non-toggle carries no pressed state at
					// all, and `aria-pressed="false"` would announce every
					// action as an un-pressed toggle button.
					pressed: isToggle ? on : null,
					testid: isToggle ? `cn-action-toggle-${entry.id}` : `cn-action-${entry.id}`,
					run: () => (isToggle ? this.onToggleClick(entry) : this.onActionClick(entry)),
				}
			})
		},

		/** Unwrapped page workspace bag. */
		workspaceCtx() {
			const c = this.workspaceRaw
			const v = (c && typeof c === 'object' && 'value' in c) ? c.value : c
			return (v && typeof v === 'object') ? v : {}
		},

		/** Unwrapped app-config bag. */
		configCtx() {
			const c = this.appConfigRaw
			const v = (c && typeof c === 'object' && 'value' in c) ? c.value : c
			return (v && typeof v === 'object') ? v : {}
		},

		/** The merged object token context (both detail-surface injects). */
		objectCtx() {
			return resolveObjectTokenContext(this.objectCtxRaw, this.detailCtxRaw) || {}
		},

		/** The full token context for url/param interpolation + local visibleWhen. */
		tokenCtx() {
			return {
				...this.objectCtx,
				workspace: this.workspaceCtx,
				config: this.configCtx,
			}
		},

		/** The effective router (explicit prop wins, else this.$router). */
		effectiveRouter() {
			return this.router || this.$router || null
		},
	},

	watch: {
		actions: {
			immediate: true,
			handler() {
				this.evaluateVisibility()
				this.initToggles()
			},
		},

		// Re-evaluate local (object-context) visibleWhen when the record loads.
		objectCtx: {
			deep: true,
			handler() { this.evaluateVisibility() },
		},

		// Push the menu-ready descriptors at a `display: "menu"` host. It fires
		// immediately so the host has the list on first paint, and again on
		// every change of visibility, toggle state or pending flag — the three
		// things that would otherwise leave a stale item in an open menu.
		menuEntries: {
			immediate: true,
			handler(entries) {
				if (this.display === 'menu') {
					/**
					 * @event entries Emitted in `display: "menu"` only, whenever the visible actions, their toggle state or their pending flags change. Payload: one menu-ready descriptor per visible action, each carrying `id`, `label`, `iconName`, `iconClass`, `disabled`, `pressed`, `testid` and a pre-bound `run()`.
					 */
					this.$emit('entries', entries)
				}
			},
		},
	},

	methods: {
		/**
		 * Heuristic MDI-name check (an `icon-`-prefixed string is a CSS class).
		 *
		 * @param {string} name The icon string.
		 * @return {boolean} True when it should render via CnIcon.
		 */
		isMdiIconName(name) {
			return typeof name === 'string' && name !== '' && !name.startsWith('icon-')
		},

		/**
		 * Evaluate every action's `visibleWhen` against the current context.
		 * Fail-safe (a broken predicate hides its action); actions without a
		 * predicate are always visible.
		 *
		 * @return {Promise<void>}
		 */
		async evaluateVisibility() {
			for (const action of this.actions || []) {
				if (!action || !action.id) {
					continue
				}
				if (!action.visibleWhen) {
					this.visibility[action.id] = true
					continue
				}
				const ok = await evaluateVisibleWhen(action.visibleWhen, this.tokenCtx)
				this.visibility[action.id] = ok
			}
		},

		/**
		 * Seed each toggle action's state from its `stateSource` endpoint
		 * (fetched through the shared endpoint engine so `@objectId` etc.
		 * resolve). No-op for non-toggle actions.
		 *
		 * @return {Promise<void>}
		 */
		async initToggles() {
			for (const action of this.actions || []) {
				if (!action || action.type !== 'toggle' || !action.id) {
					continue
				}
				this.toggleState[action.id] = false
				if (!action.stateSource || !action.stateSource.url) {
					continue
				}
				try {
					const payload = await fetchEndpointSource(action.stateSource, this.tokenCtx)
					const value = action.field ? this.readField(payload, action.field) : payload
					this.toggleState[action.id] = Boolean(value)
				} catch {
					// Leave the default (off); a failed state read never breaks the bar.
				}
			}
		},

		/**
		 * Read a dot-path off a payload (the toggle state field).
		 *
		 * @param {unknown} data The payload.
		 * @param {string} field The dot-path.
		 * @return {unknown} The value at the path.
		 */
		readField(data, field) {
			if (!field) {
				return data
			}
			return String(field).split('.').reduce((o, k) => (o === null || o === undefined ? o : o[k]), data)
		},

		/**
		 * Toggle click: flip the state OPTIMISTICALLY, write it, and revert on
		 * failure. Success/error toast via the api-call dispatch path (which
		 * also refreshes the page unless `refresh: false`).
		 *
		 * @param {object} entry The toggle action.
		 * @return {Promise<void>}
		 */
		async onToggleClick(entry) {
			if (this.togglePending[entry.id]) {
				return
			}
			const previous = Boolean(this.toggleState[entry.id])
			const next = !previous
			this.toggleState[entry.id] = next
			this.togglePending[entry.id] = true
			const writeParams = { ...(entry.params || {}) }
			if (entry.field) {
				writeParams[entry.field] = next
			}
			const result = await this.dispatch({
				type: 'api-call',
				url: entry.writeUrl,
				method: entry.method || 'PUT',
				params: writeParams,
				successMessage: entry.successMessage,
				errorMessage: entry.errorMessage,
				refresh: entry.refresh,
			})
			if (!result || result.ok === false) {
				// Revert the optimistic flip.
				this.toggleState[entry.id] = previous
			}
			this.togglePending[entry.id] = false
		},

		/**
		 * Non-toggle action click: gate on `confirm` first, else run.
		 *
		 * @param {object} entry The action.
		 * @return {void}
		 */
		onActionClick(entry) {
			if (entry.confirm === true) {
				this.confirmEntry = entry
				return
			}
			this.runAction(entry)
		},

		/**
		 * The confirm dialog's proceed handler: run the pending action, then
		 * report the outcome back to the dialog's result phase.
		 *
		 * @return {Promise<void>}
		 */
		async onConfirmProceed() {
			const entry = this.confirmEntry
			if (!entry) {
				return
			}
			const result = await this.runAction(entry)
			const dialog = this.$refs.confirmDialog
			if (dialog && typeof dialog.setResult === 'function') {
				const ok = result === undefined || (result && result.ok !== false)
				dialog.setResult(ok ? { success: true } : { error: t('nextcloud-vue', 'Action failed.') })
			} else {
				this.confirmEntry = null
			}
		},

		/**
		 * Run an action (post-confirmation). `open-form` opens the schema
		 * dialog; everything else dispatches. Returns the dispatch result so
		 * the confirm dialog can report success/failure.
		 *
		 * @param {object} entry The action.
		 * @return {Promise<unknown>} The dispatch result (undefined for open-form).
		 */
		async runAction(entry) {
			if (entry.type === 'open-form') {
				await this.openForm(entry)
				return undefined
			}
			if (entry.type === 'run-node') {
				await this.openRunNode(entry)
				return undefined
			}
			this.actionPending[entry.id] = true
			const result = await this.dispatch(entry)
			this.actionPending[entry.id] = false
			return result
		},

		/**
		 * Resolve a manifest-authored UI string through the host translate
		 * function. A pure pass-through: an empty value, or a catalogue that
		 * lacks the key, returns the input unchanged.
		 *
		 * @param {string} [value] The English source string.
		 * @return {string|undefined} The translated (or source) string.
		 */
		tr(value) {
			return value ? this.effectiveTranslate(value) : value
		},

		/**
		 * Route an action through the shared dispatcher — the pre-bound
		 * `cnDispatchAction` (router/registry/handlers/openModal already
		 * wired) when available, else a direct call with a minimal context.
		 * The token context and the host translate function are always
		 * merged in (the latter localises `api-call` success/error toasts).
		 *
		 * @param {object} action The action to dispatch.
		 * @return {Promise<unknown>} The dispatch result.
		 */
		dispatch(action) {
			const extra = { tokenCtx: this.tokenCtx, translate: this.effectiveTranslate }
			if (typeof this.cnDispatchAction === 'function') {
				return this.cnDispatchAction(action, extra)
			}
			return dispatchAction(action, {
				router: this.effectiveRouter,
				openForm: (a) => this.openForm(a),
				openRunNode: (a) => this.openRunNode(a),
				...extra,
			})
		},

		/**
		 * Open the schema-driven create dialog for an `open-form` action:
		 * fetch the schema object through the shared object store, then mount
		 * CnAdvancedFormDialog. A failed schema load toasts and no-ops.
		 *
		 * @param {object} entry The open-form action (`register`, `schema`).
		 * @return {Promise<void>}
		 */
		async openForm(entry) {
			this.formEntry = entry
			this.formSchema = null
			const register = entry.register || this.objectCtx.register || ''
			const schema = entry.schema || ''
			if (!schema) {
				this.formEntry = null
				return
			}
			try {
				const store = useObjectStore()
				const type = resolveObjectOpType(store, { register, schema })
				this.formSchema = await store.fetchSchema(type)
			} catch {
				this.formSchema = null
			}
			if (!this.formSchema) {
				const { showError } = await import('@nextcloud/dialogs')
				if (typeof showError === 'function') {
					showError(t('nextcloud-vue', 'Could not open the form.'))
				}
				this.formEntry = null
			}
		},

		/**
		 * Open (or skip) the `run-node` config dialog: GET the target node's
		 * describe endpoint (`or-flow-run-node`'s `GET .../{flowId}/{nodeId}/run`),
		 * and either mount `CnRunNodeDialog` with its declared fields, or —
		 * for a node with none — run immediately with an empty config. A
		 * failed describe call toasts and does neither (RN-4: this resolves
		 * as soon as the decision is made, never waiting on the person).
		 *
		 * @param {object} entry The run-node action (`flowId`, `nodeId`, ...).
		 * @return {Promise<void>}
		 *
		 * @spec openspec/changes/manifest-run-node-action/specs/manifest-run-node-action/spec.md#requirement-the-nodes-own-config-form-drives-the-dialog-not-a-new-token
		 */
		async openRunNode(entry) {
			const flowId = entry.flowId
			const nodeId = entry.nodeId
			if (!flowId || !nodeId) {
				// eslint-disable-next-line no-console
				console.warn('[CnActionButtons] run-node requires flowId and nodeId.', entry)
				return
			}

			// The try assigns it and the catch returns, so an initialiser here
			// would never be read.
			let fields
			try {
				const [{ default: axios }, { generateUrl }] = await Promise.all([
					import('@nextcloud/axios'),
					import('@nextcloud/router'),
				])
				const url = generateUrl(`/apps/openregister/api/flows/${encodeURIComponent(flowId)}/nodes/${encodeURIComponent(nodeId)}/run`)
				const response = await axios.get(url)
				fields = Array.isArray(response.data && response.data.configForm) ? response.data.configForm : []
			} catch (error) {
				const { showError } = await import('@nextcloud/dialogs')
				const serverMessage = error && error.response && error.response.data
					&& (error.response.data.error || error.response.data.message)
				if (typeof showError === 'function') {
					showError(serverMessage || t('nextcloud-vue', 'Could not open this action.'))
				}
				return
			}

			if (!fields.length) {
				// Empty (or undeclared) form: the "empty form is itself
				// information" rule flow-node-config-forms already established
				// for the editor — run immediately, no dialog.
				this.actionPending[entry.id] = true
				await this.runNodePost(entry, {})
				this.actionPending[entry.id] = false
				return
			}

			this.runNodeFields = fields
			this.runNodeEntry = entry
		},

		/**
		 * The run-node dialog's confirm: POST the collected config, close the
		 * dialog either way (success or failure both end the interaction —
		 * a failure already toasted its own message).
		 *
		 * @param {object} config The dialog's collected field values.
		 * @return {Promise<void>}
		 *
		 * @spec openspec/changes/manifest-run-node-action/specs/manifest-run-node-action/spec.md#requirement-a-run-node-action-invokes-one-flow-node-against-the-page-object
		 */
		async onRunNodeConfirm(config) {
			const entry = this.runNodeEntry
			if (!entry) {
				return
			}
			this.actionPending[entry.id] = true
			await this.runNodePost(entry, config)
			this.actionPending[entry.id] = false
			this.closeRunNode()
		},

		/**
		 * POST the run-node call through the shared dispatcher's `postRunNode`
		 * (subject/register/schema resolution, fail-closed, toast + refresh —
		 * see its own docblock in actionsDispatcher.js).
		 *
		 * @param {object} entry The run-node action.
		 * @param {object} config The node's collected config.
		 * @return {Promise<{ok: boolean}>}
		 */
		runNodePost(entry, config) {
			return postRunNode(entry, { tokenCtx: this.tokenCtx, translate: this.effectiveTranslate }, config)
		},

		/** Close the run-node dialog without running anything. */
		closeRunNode() {
			this.runNodeEntry = null
			this.runNodeFields = []
		},

		/**
		 * The create dialog's confirm: save the new object through the shared
		 * object store, report the result phase, toast, refresh the page, and
		 * navigate to `onSuccessRoute` when set — merging the saved object's id
		 * into the route params (via `buildOnSuccessRoute`) so the navigation
		 * can deep-link to the created object's detail page.
		 *
		 * @param {object} formData The dialog's form payload.
		 * @param {object|null} [dynamic] The answers to the schema's data-driven questions (`{ answers, declarations }`), when it has any.
		 * @return {Promise<void>}
		 */
		async onFormConfirm(formData, dynamic = null) {
			const entry = this.formEntry
			const dialog = this.$refs.formDialog
			const register = this.formRegister
			const schema = (entry && entry.schema) || ''
			try {
				const store = useObjectStore()
				const type = resolveObjectOpType(store, { register, schema })
				// A schema can require a field the form cannot supply — a
				// server-minted foreign key, say — in which case a straight
				// saveObject 400s. `createOverride` names a registry handler
				// that owns the persist instead (the CnIndexPage precedent).
				const override = resolveCreateOverrideHandler(
					entry && entry.createOverride,
					this.cnRegistry,
					this.cnCustomComponents,
				)
				const payload = { ...formData }
				// Array-mode declarations fold their answers into the parent's
				// own payload, so the whole record saves in ONE write. The
				// child-record mode below cannot do that (the rows need the
				// parent's id), which is why it writes afterwards and can leave
				// answers behind if that second write fails.
				this.foldArrayAnswers(payload, dynamic)
				const saved = override
					? await override(payload, { register, schema, type })
					: await store.saveObject(type, payload)
				if (!saved) {
					throw new Error('save rejected')
				}
				await this.saveDynamicAnswers(store, register, dynamic, saved)
				if (dialog && typeof dialog.setResult === 'function') {
					dialog.setResult({ success: true })
				}
				const { showSuccess } = await import('@nextcloud/dialogs')
				if (typeof showSuccess === 'function') {
					showSuccess((entry && entry.successMessage) || t('nextcloud-vue', 'Saved.'))
				}
				const { emit } = await import('@nextcloud/event-bus')
				emit('cn:page:refresh', {})
				/**
				 * @event created Emitted after an open-form action saves. Payload: the created object.
				 */
				this.$emit('created', saved)
				if (entry && entry.onSuccessRoute && this.effectiveRouter) {
					const location = buildOnSuccessRoute(entry.onSuccessRoute, saved)
					if (location) {
						this.effectiveRouter.push(location).catch(() => {})
					}
				}
			} catch (e) {
				if (dialog && typeof dialog.setResult === 'function') {
					dialog.setResult({ error: (e && e.message) || t('nextcloud-vue', 'Save failed.') })
				}
			}
		},

		/**
		 * Write the answers to a schema's data-driven questions, once the
		 * object they belong to exists.
		 *
		 * This runs AFTER the parent save and deliberately does not roll it
		 * back on failure: a case that exists without one of its custom values
		 * is recoverable by editing it, whereas discarding a case the user
		 * believes they filed is not. A failure here surfaces as the dialog's
		 * error so it is never silent.
		 *
		 * @param {object} store The object store.
		 * @param {string} register The register the object was saved in.
		 * @param {object|null} dynamic The `{ answers, declarations }` payload.
		 * @param {object} saved The saved parent object.
		 * @return {Promise<void>}
		 */
		async saveDynamicAnswers(store, register, dynamic, saved) {
			if (!dynamic || !Array.isArray(dynamic.answers) || dynamic.answers.length === 0) {
				return
			}
			const objectId = saved && (saved.id || saved.uuid)
			if (!objectId) {
				return
			}
			const declarations = dynamic.declarations || []
			for (const { key, config } of declarations) {
				const values = config && config.values
				if (!values || !values.schema) {
					continue
				}
				// Already written as part of the parent payload.
				if (usesArrayValues(config)) {
					continue
				}
				// An answer belongs to exactly one declaration. With a single
				// declaration every answer carries its key anyway; the filter
				// only matters once a schema has two, where writing an answer
				// to both value schemas would invent a row nobody entered.
				const mine = declarations.length === 1
					? dynamic.answers
					: dynamic.answers.filter((a) => a.declarationKey === key)
				const rows = valueRecordsFor(mine, config, objectId)
				if (rows.length === 0) {
					continue
				}
				const valueRegister = values.register || register
				const type = resolveObjectOpType(store, { register: valueRegister, schema: values.schema })
				for (const row of rows) {
					await store.saveObject(type, row)
				}
			}
		},

		/**
		 * Fold array-mode dynamic answers into the parent payload.
		 *
		 * @param {object} payload The parent object payload, mutated in place.
		 * @param {object|null} dynamic The `{ answers, declarations }` payload.
		 * @return {void}
		 */
		foldArrayAnswers(payload, dynamic) {
			if (!dynamic || !Array.isArray(dynamic.answers) || dynamic.answers.length === 0) {
				return
			}
			const declarations = dynamic.declarations || []
			for (const { key, config } of declarations) {
				if (!usesArrayValues(config)) {
					continue
				}
				const mine = declarations.length === 1
					? dynamic.answers
					: dynamic.answers.filter((a) => a.declarationKey === key)
				const entries = valueArrayFor(mine, config, dynamic.definitions || [])
				if (entries.length) {
					payload[config.values.arrayKey] = entries
				}
			}
		},

		/**
		 * Close the create dialog.
		 *
		 * @return {void}
		 */
		closeForm() {
			this.formEntry = null
			this.formSchema = null
		},
	},
}
</script>

<style scoped>
.cn-action-buttons {
	display: inline-flex;
	align-items: center;
	gap: 8px;
	flex-wrap: wrap;
}

/* In menu mode the only children are dialogs, so the wrapper must not be a
   box: an empty inline-flex child still eats one of the header's flex gaps
   and pushes the Actions menu away from the Edit button beside it. */
.cn-action-buttons--menu {
	display: contents;
}
</style>
